import { useEffect, useRef, useState } from 'react';

interface UseCompletionToggleEffectOptions {
  isCompleted: boolean;
  enabled?: boolean;
  onToggle?: (isCompleted: boolean) => void;
  saveReport?: (options?: { silent?: boolean }) => Promise<void>;
  saveDelayMs?: number;
}

interface UseCompletionToggleEffectResult {
  /** True from the moment the flag flips until the triggered save settles. */
  isSavingCompletion: boolean;
}

/**
 * Reacts to a section's isCompleted flag flipping (not its initial value on mount),
 * running a side effect (e.g. collapse the section) and triggering a delayed, silent
 * save. The delay lets the toggle's pulse animation finish before saveReport's
 * synchronous flush work can stall the main thread.
 *
 * Returns the in-flight state of that save so the toggle can show progress in place
 * of the tick, which is why the save stays silent (no success toast).
 */
export function useCompletionToggleEffect({
  isCompleted,
  enabled = true,
  onToggle,
  saveReport,
  saveDelayMs = 550,
}: UseCompletionToggleEffectOptions): UseCompletionToggleEffectResult {
  const lastReactedRef = useRef<boolean | undefined>(undefined);
  const [isSavingCompletion, setIsSavingCompletion] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    if (lastReactedRef.current === undefined) {
      lastReactedRef.current = isCompleted;
      return;
    }
    if (lastReactedRef.current === isCompleted) return;
    lastReactedRef.current = isCompleted;

    onToggle?.(isCompleted);

    if (!saveReport) return;

    // Covers the debounce window as well as the request itself, so the toggle shows
    // progress for the whole time the new state is unpersisted.
    let cancelled = false;
    setIsSavingCompletion(true);

    const timeoutId = setTimeout(async () => {
      try {
        await saveReport({ silent: true });
      } finally {
        // A re-toggle mid-save owns the indicator by then; don't clear it from here.
        if (!cancelled) setIsSavingCompletion(false);
      }
    }, saveDelayMs);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      setIsSavingCompletion(false);
    };
  }, [isCompleted, enabled]);

  return { isSavingCompletion };
}
